import { useState } from 'react';
import { frictionSubtypeLabel } from '../constants';
import type { Ping, RescueSession } from '../types';

type PingDetailModalProps = {
  ping: Ping;
  rescues: RescueSession[];
  pingTypeLabel: (type: Ping['type']) => string;
  rescueKindLabel: (kind: RescueSession['kind']) => string;
  interruptionLabel: (source: Ping['interruption_source']) => string;
  labels: {
    title: string;
    close: string;
    type: string;
    stuckBeforePing: string;
    subtype: string;
    interruptedBy: string;
    completed: string;
    canceled: string;
    tinyWin: string;
    prep: string;
    phone: string;
    tabs: string;
    doc: string;
    timer: string;
    addNote: string;
    saveNote: string;
    deletePing: string;
    na: string;
  };
  onClose: () => void;
  onDelete: () => void;
  onSaveNote: (note: string) => void;
};

export function PingDetailModal({
  ping,
  rescues,
  pingTypeLabel,
  rescueKindLabel,
  interruptionLabel,
  labels,
  onClose,
  onDelete,
  onSaveNote
}: PingDetailModalProps) {
  const [note, setNote] = useState(ping.note ?? '');
  const pingTypeText = pingTypeLabel(ping.type);

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <h3>{labels.title}</h3>
          <button onClick={onClose}>{labels.close}</button>
        </div>
        <div className="detail-datestamp">{new Date(ping.ts_ms).toLocaleString()}</div>
        <div className="detail-lede">{pingTypeText}</div>
        <div className="detail-facts">
          <div className="detail-fact">
            <span className="detail-fact-label">{labels.type}</span>
            <span>{pingTypeText}</span>
          </div>
          <div className="detail-fact">
            <span className="detail-fact-label">{labels.stuckBeforePing}</span>
            <span>{ping.friction_started_ms ? `${Math.max(1, Math.round((ping.ts_ms - ping.friction_started_ms) / 60000))}m` : labels.na}</span>
          </div>
          <div className="detail-fact">
            <span className="detail-fact-label">{labels.subtype}</span>
            <span>{frictionSubtypeLabel(ping.subtype)}</span>
          </div>
          <div className="detail-fact">
            <span className="detail-fact-label">{labels.interruptedBy}</span>
            <span>{interruptionLabel(ping.interruption_source)}</span>
          </div>
        </div>
        <div className="stack">
          {rescues.map((r) => (
            <div key={r.id} className="card detail-rescue-card">
              <div className="detail-rescue-title">
                <span>{rescueKindLabel(r.kind)}</span>
                <span className="detail-rescue-status">{r.completed ? labels.completed : labels.canceled}</span>
              </div>
              {r.tiny_win ? <div className="detail-rescue-body">{`${labels.tinyWin}: ${r.tiny_win}`}</div> : null}
              {r.duration_s > 0 &&
                (r.prep_phone_silenced || r.prep_tabs_closed || r.prep_doc_open || r.prep_timer_visible) && (
                  <div className="detail-rescue-body">
                    {`${labels.prep} ${r.prep_phone_silenced ? `${labels.phone} ` : ''}${r.prep_tabs_closed ? `${labels.tabs} ` : ''}${
                      r.prep_doc_open ? `${labels.doc} ` : ''
                    }${r.prep_timer_visible ? labels.timer : ''}`.trim()}
                  </div>
                )}
            </div>
          ))}
        </div>
        <details>
          <summary>{labels.addNote}</summary>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} />
          <button onClick={() => onSaveNote(note)}>{labels.saveNote}</button>
        </details>
        <button className="danger" onClick={onDelete}>
          {labels.deletePing}
        </button>
      </div>
    </div>
  );
}
